import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, MenuItem, Paper, Stack, Step, StepLabel,
  Stepper, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { IntakeField, IntakeForm, IntakeStep, Invite } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";

/**
 * Kartoteka jednego wykonawcy i dwie ścieżki awaryjne z §4.2 i z ryzyk etapu 1.
 *
 * **Kreator w imieniu wykonawcy.** Część obsady portalu nie wypełni — nie ma
 * smartfona, jest na planie bez zasięgu, podyktuje dane przez telefon. Bez
 * tego ekranu kierownik i tak zbierze te dane, tyle że w arkuszu obok
 * systemu; wtedy platforma przestaje być jedynym miejscem, w którym stoi
 * prawda, a to jest cały jej sens.
 *
 * **Zmiana numeru telefonu.** Nie jest polem edycyjnym w kartotece, bo nie
 * jest edycją danych: numer to jedyny czynnik posiadania w torze tożsamości
 * wykonawcy, więc jego zmiana przekierowuje cudze kody dostępu. Stąd osobne
 * okno z kanałem weryfikacji i wymaganą notatką.
 *
 * Pola kreatora opisuje rdzeń — ten ekran je rysuje i nie zna reguł, według
 * których powstały.
 */

export function PersonPage() {
  const { id = "" } = useParams();
  const { can } = useAuth();
  const kolejka = useQueryClient();
  const [krok, setKrok] = useState(0);
  const [telefon, setTelefon] = useState(false);
  const [link, setLink] = useState<Invite | null>(null);
  const [blad, setBlad] = useState<ApiError | null>(null);

  const { data } = useQuery({
    queryKey: ["intake", id],
    queryFn: () => api.get<IntakeForm>(`/people/${id}/intake`),
    enabled: Boolean(id) && can("contractor.edit"),
  });

  const zaproszenie = useMutation({
    mutationFn: () => api.post<Invite>(`/people/${id}/invite`, {}),
    onSuccess: (wynik) => { setLink(wynik); setBlad(null); },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const zatwierdz = useMutation({
    mutationFn: () => api.post(`/people/${id}/intake/submit`, {}),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["intake", id] });
      kolejka.invalidateQueries({ queryKey: ["people"] });
      setBlad(null);
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  if (!can("contractor.edit")) {
    return <Alert severity="info">
      Uzupełnianie danych wykonawcy wymaga uprawnienia do edycji kartoteki.
    </Alert>;
  }
  if (!data) return null;

  const kroki = data.steps;
  const biezacy = kroki[Math.min(krok, kroki.length - 1)];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <BackButton fallback="/wykonawcy" />
        <Typography variant="h6">{data.display_name}</Typography>
        {data.project && <Chip size="small" label={`${data.project} · ${data.role}`} />}
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setTelefon(true)}>Zmień numer telefonu</Button>
        <Button onClick={() => zaproszenie.mutate()} disabled={zaproszenie.isPending}>
          Wystaw link do portalu
        </Button>
      </Stack>

      {blad && <Alert severity="error">
        {blad.message}
        {blad.body.problems?.length > 0 && (
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {blad.body.problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
      </Alert>}

      {link && (
        <Alert severity="success" onClose={() => setLink(null)}>
          {/* Token widać jeden jedyny raz — w bazie leży wyłącznie jego skrót. */}
          <Typography variant="body2">
            Przekaż ten link wykonawcy tą samą drogą, którą potwierdziłeś jego
            tożsamość. Sam link nie wpuszcza: kod jednorazowy idzie na numer
            z kartoteki.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, wordBreak: "break-all" }}>
            <code>{link.url || link.token}</code>
          </Typography>
        </Alert>
      )}

      <Alert severity={data.confirmed_via ? "success" : "warning"}>
        {data.confirmed_via === "staff"
          ? `Dane wpisał zespół produkcji (${data.last_confirmed_at?.slice(0, 10)}).`
          : data.confirmed_via === "portal"
            ? `Dane potwierdził wykonawca przez portal (${data.last_confirmed_at?.slice(0, 10)}).`
            : "Danych jeszcze nikt nie potwierdził."}
        {data.missing.length > 0 && ` Brakuje: ${data.missing.join(", ")}.`}
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stepper activeStep={krok} sx={{ mb: 3 }}>
          {kroki.map((k) => <Step key={k.key}><StepLabel>{k.title}</StepLabel></Step>)}
        </Stepper>

        <KrokFormularza personId={id} krok={biezacy}
                       ostatni={krok + 1 === kroki.length}
                       onWstecz={() => setKrok((n) => Math.max(0, n - 1))}
                       onDalej={() => setKrok((n) => Math.min(kroki.length - 1, n + 1))}
                       onZatwierdz={() => zatwierdz.mutate()} />
      </Paper>

      <ZmianaTelefonu personId={id} open={telefon} onClose={() => setTelefon(false)} />
    </Stack>
  );
}

function KrokFormularza({ personId, krok, ostatni, onWstecz, onDalej, onZatwierdz }: {
  personId: string;
  krok: IntakeStep;
  ostatni: boolean;
  onWstecz: () => void;
  onDalej: () => void;
  onZatwierdz: () => void;
}) {
  const [wartosci, setWartosci] = useState<Record<string, string | boolean>>(
    () => Object.fromEntries(krok.fields.map((p) => [p.name, wartoscPola(p)])),
  );
  const [blad, setBlad] = useState<ApiError | null>(null);
  const kolejka = useQueryClient();

  const zapisz = useMutation({
    mutationFn: () => api.post(`/people/${personId}/intake`,
      { step: krok.key, values: wartosci }),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["intake", personId] });
      setBlad(null);
      if (ostatni) onZatwierdz(); else onDalej();
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Stack spacing={2}>
      {krok.intro && <Typography variant="body2" color="text.secondary">{krok.intro}</Typography>}

      {krok.fields.map((pole) => (
        <PoleKreatora key={pole.name} pole={pole}
                      wartosc={wartosci[pole.name]}
                      blad={blad?.field(pole.name)}
                      onZmiana={(v) => setWartosci({ ...wartosci, [pole.name]: v })} />
      ))}

      <Divider />
      <Stack direction="row" spacing={1}>
        <Button onClick={onWstecz}>Wstecz</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={() => zapisz.mutate()}
                disabled={zapisz.isPending}>
          {ostatni ? "Zapisz i zatwierdź dane" : "Dalej"}
        </Button>
      </Stack>
    </Stack>
  );
}

function wartoscPola(pole: IntakeField): string | boolean {
  return pole.type === "checkbox" ? Boolean(pole.value) : pole.value;
}

function PoleKreatora({ pole, wartosc, blad, onZmiana }: {
  pole: IntakeField;
  wartosc: string | boolean;
  blad?: string;
  onZmiana: (v: string | boolean) => void;
}) {
  if (pole.type === "checkbox") {
    return (
      <Box>
        <FormControlLabel
          control={<Checkbox checked={Boolean(wartosc)}
                             onChange={(e) => onZmiana(e.target.checked)} />}
          label={pole.label} />
        {(blad || pole.help) && (
          <Typography variant="caption" color={blad ? "error" : "text.secondary"}
                      display="block">
            {blad ?? pole.help}
          </Typography>
        )}
      </Box>
    );
  }

  const wspolne = {
    label: pole.label,
    value: String(wartosc ?? ""),
    error: Boolean(blad),
    helperText: blad ?? pole.help,
    disabled: pole.readonly,
    fullWidth: true,
  };

  if (pole.type === "select") {
    return (
      <TextField select {...wspolne} onChange={(e) => onZmiana(e.target.value)}>
        <MenuItem value="">— wybierz —</MenuItem>
        {pole.options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField {...wspolne}
               multiline={pole.type === "textarea"}
               minRows={pole.type === "textarea" ? 2 : undefined}
               onChange={(e) => onZmiana(e.target.value)} />
  );
}

const KANALY = [
  { value: "rozmowa", label: "Rozmowa na dotychczasowy numer" },
  { value: "osobiscie", label: "Osobiście, na planie" },
  { value: "kierownik", label: "Potwierdzenie kierownika produkcji" },
  { value: "inny", label: "Inny kanał (opisany w notatce)" },
];

function ZmianaTelefonu({ personId, open, onClose }: {
  personId: string; open: boolean; onClose: () => void;
}) {
  const [form, setForm] = useState({ phone: "", channel: "rozmowa", note: "" });
  const [blad, setBlad] = useState<ApiError | null>(null);
  const kolejka = useQueryClient();

  const zapisz = useMutation({
    mutationFn: () => api.post(`/people/${personId}/phone`, form),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["people"] });
      setForm({ phone: "", channel: "rozmowa", note: "" });
      setBlad(null);
      onClose();
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Zmiana numeru telefonu</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {blad && !Object.keys(blad.body.fields).length && (
            <Alert severity="error">{blad.message}</Alert>
          )}
          <Alert severity="warning">
            Na ten numer pójdą kody dostępu do portalu. Zmieniaj go dopiero po
            potwierdzeniu tożsamości innym kanałem — numer PESEL nie jest
            tajemnicą i sam w sobie nie dowodzi niczego.
          </Alert>
          <TextField label="Nowy numer" value={form.phone}
                     error={Boolean(blad?.field("phone"))}
                     helperText={blad?.field("phone")}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <TextField select label="Jak potwierdzono tożsamość" value={form.channel}
                     onChange={(e) => setForm({ ...form, channel: e.target.value })}>
            {KANALY.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
          </TextField>
          <TextField label="Notatka" multiline minRows={2} value={form.note}
                     error={Boolean(blad?.field("note"))}
                     helperText={blad?.field("note")
                       ?? "Kto i jak potwierdził. Ta notatka jest treścią kontroli."}
                     onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={() => zapisz.mutate()}
                disabled={zapisz.isPending}>Zmień numer</Button>
      </DialogActions>
    </Dialog>
  );
}
